import { LineItem } from '@vendasta/sales-orders';
import { UILineItem } from '@vendasta/store';
import { combineLatest, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  publishReplay,
  refCount,
  shareReplay,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { Package, PackageService } from '@vendasta/marketplace-packages';
import { App, AppKey, AppPartnerService } from '@galaxy/marketplace-apps';
import { INVALID_ITEM } from '../../shared/constants';
import { convertSelectedAppToLineItem, packageToUILineItem } from '../../shared/utils';
import { Inject, Injectable } from '@angular/core';
import { AppSdkService, PackageApiService } from './line-items.interface';
import { MARKET_ID_TOKEN, PARTNER_ID_TOKEN } from '@vendasta/sales-ui';
import equal from 'fast-deep-equal';

@Injectable()
export class LineItemsAdapterService {
  constructor(
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID_TOKEN) private readonly marketId$: Observable<string>,
    @Inject(PackageService) private readonly packageSdk: PackageApiService,
    @Inject(AppPartnerService) private readonly appSdk: AppSdkService,
  ) {}

  lineItemsToUILineItems(input: {
    lineItems$: Observable<LineItem[]>;
    partnerId?: string;
    marketId?: string;
    defaultCurrency?: string;
  }): Observable<UILineItem[]> {
    const partnerId$ = input.partnerId ? of(input.partnerId) : this.partnerId$;
    const marketId$ = input.marketId ? of(input.marketId) : this.marketId$;

    // call out for package information
    const packages$ = input.lineItems$.pipe(
      switchMap((lineItems) => {
        const packageLineItems = lineItems.filter((li) => !!li.packageId);
        if (packageLineItems.length > 0) {
          return this.packageSdk.getMulti(packageLineItems.map((li) => li.packageId));
        }
        return of([] as Package[]);
      }),
      publishReplay(1),
      refCount(),
    );

    const lineItemsWithPartnerIdAndMarketId$ = combineLatest([partnerId$, marketId$, input.lineItems$]).pipe(
      map(([partnerId, marketId, lineItems]) => ({ partnerId, marketId, lineItems })),
    );

    // call out for app information
    const appsAndAddons$ = lineItemsWithPartnerIdAndMarketId$.pipe(
      switchMap(({ lineItems, partnerId, marketId }) => {
        const appsAndAddonsLineItems = lineItems.filter((li) => !!li.appKey);
        const appsAndAddonsKeys = appsAndAddonsLineItems.map((app) => new AppKey(app.appKey));
        if (appsAndAddonsKeys.length > 0) {
          return this.appSdk.getMulti(appsAndAddonsKeys, partnerId, marketId, {}, true);
        }
        return of([] as App[]);
      }),
      map((apps) => apps.filter((a) => !!a?.key?.appId)),
      publishReplay(1),
      refCount(),
    );

    // resolve the package/app information into the line item
    return combineLatest([
      packages$.pipe(distinctUntilChanged((previous, current) => equal(previous, current))),
      appsAndAddons$.pipe(distinctUntilChanged()),
    ]).pipe(
      withLatestFrom(input.lineItems$),
      map(([[packages, appsAndAddons], lineItems]) => {
        return lineItems.map((li) => {
          let uiLineItem = INVALID_ITEM;
          if (li.packageId) {
            const pack = packages.find((p) => p.packageId === li.packageId);
            if (pack) {
              uiLineItem = packageToUILineItem(pack, input.defaultCurrency);
            }
          } else {
            const app = appsAndAddons.find(
              (a) => a.key.appId === li.appKey.appId && (a.key.editionId || '') === (li.appKey.editionId || ''),
            );
            if (app) {
              uiLineItem = convertSelectedAppToLineItem(app, input.defaultCurrency);
            }
          }
          uiLineItem.lineItem = li;
          return uiLineItem;
        });
      }),
      shareReplay(1),
    );
  }
}
